import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { PollingLocation } from '../types/PollingLocation';

export interface PollingLocationsState {
  core: {
    locations: PollingLocation[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
  };
  byElection: {
    [electionId: string]: {
      locations: PollingLocation[];
      loading: boolean;
      loaded: boolean;
      error: string | null;
      importingById: Record<string, boolean>;
    };
  };
}

const initialElectionState = {
  locations: [],
  loading: false,
  loaded: false,
  error: null,
  importingById: {},
};

const initialState: PollingLocationsState = {
  core: {
    locations: [],
    loading: false,
    loaded: false,
    error: null,
  },
  byElection: {},
};

const pollingLocationsSlice = createSlice({
  name: 'pollingLocations',
  initialState,
  reducers: {
    fetchPollingLocationsStart: (state, action: PayloadAction<{ electionId?: string }>) => {
      if (action.payload.electionId) {
        if (!state.byElection[action.payload.electionId]) {
          state.byElection[action.payload.electionId] = { ...initialElectionState };
        }
        state.byElection[action.payload.electionId].loading = true;
        state.byElection[action.payload.electionId].error = null;
        state.byElection[action.payload.electionId].loaded = false;
      } else {
        state.core.loading = true;
        state.core.error = null;
        state.core.loaded = false;
      }
    },
    fetchPollingLocationsSuccess: (state, action: PayloadAction<{ locations: PollingLocation[]; electionId?: string }>) => {
      const { locations, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].locations = locations;
        state.byElection[electionId].loading = false;
        state.byElection[electionId].loaded = true;
      } else {
        state.core.locations = locations;
        state.core.loading = false;
        state.core.loaded = true;
      }
    },
    fetchPollingLocationsFailure: (state, action: PayloadAction<{ error: string; electionId?: string }>) => {
      const { error, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].error = error;
        state.byElection[electionId].loading = false;
        state.byElection[electionId].loaded = true;
      } else {
        state.core.error = error;
        state.core.loading = false;
        state.core.loaded = true;
      }
    },
    importPollingLocationToElectionStart: (state, action: PayloadAction<{ electionId: string; pollingLocationIds: string[] }>) => {
      const { electionId, pollingLocationIds } = action.payload;
      if (!pollingLocationIds.length) return;
      if (!state.byElection[electionId]) {
        state.byElection[electionId] = { ...initialElectionState };
      }
      for (const id of pollingLocationIds) {
        state.byElection[electionId].importingById[id] = true;
      }
    },
    importPollingLocationToElectionComplete: (state, action: PayloadAction<{ electionId: string; pollingLocationIds: string[] }>) => {
      const { electionId, pollingLocationIds } = action.payload;
      const election = state.byElection[electionId];
      if (!election) return;
      for (const id of pollingLocationIds) {
        delete election.importingById[id];
      }
    },
    importPollingLocationToElectionFailure: (state, action: PayloadAction<{ electionId: string; error: string; pollingLocationIds: string[] }>) => {
      const { electionId, error } = action.payload;
      if (!state.byElection[electionId]) {
        state.byElection[electionId] = { ...initialElectionState };
      }
      state.byElection[electionId].error = error;
    },
  },
});

export const {
  fetchPollingLocationsStart,
  fetchPollingLocationsSuccess,
  fetchPollingLocationsFailure,
  importPollingLocationToElectionStart,
  importPollingLocationToElectionComplete,
  importPollingLocationToElectionFailure,
} = pollingLocationsSlice.actions;

export default pollingLocationsSlice.reducer;
